import { useEffect, useRef } from 'react';
import { subscribeToTable } from '@/lib/supabase-client';
import { useAuth } from '@/providers/auth-provider';
import { useNotificationSound } from './use-notification-sound';
import { useChannels } from '@socialflows/shared-lib';

export interface TicketCreatedEvent {
  ticketId: string;
  accountId: string;
  affectedFolders: string[];
  ticket: {
    status: string;
    priority: string;
    tags: string[];
    assignedTo: string | null;
    folderId: string | null;
    channelId: string;
    createdAtNetwork: string | null;
  };
}

interface UseTicketEventsOptions {
  userId?: string;
  onTicketCreated?: (event: TicketCreatedEvent) => void;
}

function computeAffectedFolders(
  ticket: {
    status: string;
    priority: string;
    tags: string[];
    assignedTo: string | null;
    folderId: string | null;
  },
  userId?: string
): string[] {
  if (ticket.status === 'CLOSED') return [];

  const folders: string[] = [];

  if (ticket.priority === 'URGENT') folders.push('urgent');
  if (ticket.tags?.includes('customer:vip')) folders.push('vip');
  if (ticket.tags?.includes('intent:buy')) folders.push('buyers');
  if (ticket.folderId) folders.push(`custom:${ticket.folderId}`);
  if (userId && ticket.assignedTo === userId) folders.push('myTickets');
  folders.push('all');

  return folders;
}

export function useTicketEvents(options: UseTicketEventsOptions = {}) {
  const { profile } = useAuth();
  const { playSound } = useNotificationSound();
  const { data: channels = [] } = useChannels();
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const channelsMapRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const map = new Map<string, string>();
    for (const ch of channels) {
      map.set(ch.id, ch.createdAt);
    }
    channelsMapRef.current = map;
  }, [channels]);

  useEffect(() => {
    if (!profile?.accountId) return;

    const channel = subscribeToTable(
      'tickets',
      async (payload: any) => {
        if (payload.eventType !== 'INSERT') return;

        const t = payload.new;
        if (t.is_meta_ticket) return;
        if (t.is_own) return;

        const ticketData = {
          status: t.status,
          priority: t.priority || 'LOW',
          tags: t.tags || [],
          assignedTo: t.assigned_to ?? null,
          folderId: t.folder_id ?? null,
          channelId: t.channel_id,
          createdAtNetwork: t.created_at_network ?? null,
        };

        const affectedFolders = computeAffectedFolders(ticketData, optionsRef.current.userId);

        if (affectedFolders.length === 0) return;

        const createdAtNetwork = ticketData.createdAtNetwork
          ? new Date(ticketData.createdAtNetwork).getTime()
          : null;

        let shouldPlaySound = true;

        if (createdAtNetwork && ticketData.channelId) {
          const channelCreatedAt = channelsMapRef.current.get(ticketData.channelId);
          if (channelCreatedAt) {
            const connectedTime = new Date(channelCreatedAt).getTime();
            if (createdAtNetwork < connectedTime) {
              shouldPlaySound = false;
            }
          }
        }

        if (shouldPlaySound) {
          playSound({ type: 'TICKET_CREATED', priority: ticketData.priority?.toLowerCase() });
        }

        if (optionsRef.current.onTicketCreated) {
          optionsRef.current.onTicketCreated({
            ticketId: t.id,
            accountId: t.account_id,
            affectedFolders,
            ticket: ticketData,
          });
        }
      },
      { column: 'account_id', value: profile.accountId }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [profile?.accountId, playSound]);

  return {};
}
